import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, MapPin, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatUsd,
  getInitials,
  getLeadStatus,
  LEAD_STATUS_META,
} from '@/lib/clientsData';

const typeStyles = {
  client:
    'bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30',
  lead:
    'bg-[hsl(var(--info))]/12 text-[hsl(var(--info))] border-[hsl(var(--info))]/30',
};

const statusTone = {
  won: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40',
  pending:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40',
  learned:
    'bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/40',
};

export const ContactRow = ({ contact, onOpen }) => {
  const { t } = useTranslation();
  const isLead = contact?.type === 'lead';
  const typeBadge = typeStyles[contact?.type] || typeStyles.client;
  const value = isLead ? contact?.lead_value : contact?.ltv;
  const valueLabel = isLead
    ? t('clientsLeads.stats.pipelineValue')
    : t('clientsLeads.stats.clientLtv');

  return (
    <button
      type="button"
      onClick={() => onOpen?.(contact)}
      className="w-full text-left rounded-md border border-border bg-[hsl(var(--background))]/40 hover:border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--muted))]/40 transition-colors px-4 py-3.5 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
      data-testid={`contact-row-${contact?.id}`}
      aria-label={t('clientsLeads.row.viewProfile')}
    >
      <div className="flex items-center gap-4">
        <Avatar className="h-10 w-10 border border-[hsl(var(--primary))]/25 shrink-0">
          <AvatarFallback className="bg-[hsl(var(--muted))] text-[hsl(var(--primary))] text-xs font-semibold">
            {getInitials(contact?.name)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm sm:text-base font-semibold text-foreground truncate">
              {contact?.name || t('clientsLeads.row.viewProfile')}
            </span>
            <Badge
              variant="outline"
              className={cn('uppercase text-[10px] tracking-[0.16em]', typeBadge)}
            >
              {isLead ? t('clientsLeads.row.lead') : t('clientsLeads.row.client')}
            </Badge>
            {isLead && contact?.lead_type ? (
              <Badge
                variant="outline"
                className="uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--muted))] text-muted-foreground border-border"
              >
                {contact.lead_type}
              </Badge>
            ) : null}
            {isLead ? (
              <Badge
                variant="outline"
                className={cn(
                  'uppercase text-[10px] tracking-[0.16em]',
                  statusTone[getLeadStatus(contact)]
                )}
                data-testid={`contact-row-status-${contact?.id}`}
              >
                {LEAD_STATUS_META[getLeadStatus(contact)]?.label ||
                  getLeadStatus(contact)}
              </Badge>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {contact?.phone ? (
              <span className="inline-flex items-center gap-1.5 truncate">
                <Phone size={12} strokeWidth={2} />
                <span className="truncate">{contact.phone}</span>
              </span>
            ) : null}
            {contact?.email ? (
              <span className="inline-flex items-center gap-1.5 truncate max-w-[220px]">
                <Mail size={12} strokeWidth={2} />
                <span className="truncate">{contact.email}</span>
              </span>
            ) : null}
            {contact?.address ? (
              <span className="inline-flex items-center gap-1.5 truncate max-w-[260px]">
                <MapPin size={12} strokeWidth={2} />
                <span className="truncate">{contact.address}</span>
              </span>
            ) : null}
          </div>
        </div>

        <div className="hidden sm:flex flex-col items-end shrink-0">
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {valueLabel}
          </span>
          <span
            className={cn(
              'mt-0.5 text-sm font-semibold',
              isLead ? 'text-[hsl(var(--success))]' : 'text-foreground'
            )}
          >
            {formatUsd(value)}
          </span>
        </div>

        <ChevronRight
          size={16}
          className="shrink-0 text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors"
        />
      </div>
    </button>
  );
};

export default ContactRow;
